/**
 * Транспонована таблиця розділу «Фінансовий результат»: періоди (години 01–24
 * або дати) — у СТОВПЦЯХ, показники — РЯДКАМИ, останній стовпець «Всього».
 * ОБИДВА крайні стовпці ЛИПКІ: перший (значки показників) — sticky left, останній
 * («Всього») — sticky right; годинні стовпці горизонтально скролюються МІЖ ними.
 * Дизайн-токени (var(--)), світла/темна.
 *
 * Перший стовпець — значки замість назв (як у таблиці «Погодинні дані» на
 * /dispatch/forecast-mms): назва рядка йде в title-тултіп, а не в тексті —
 * колонка лишається вузькою незалежно від довжини підпису показника.
 *
 * activeColKey — та сама година/дата, що під курсором на графіку зверху:
 * підсвічує відповідну колонку (та сама техніка, що й marker у
 * ForecastMmsTable — заливка без вертикалі) і, якщо колонка зараз поза видимою
 * зоною горизонтального скролу, докручує таблицю так, щоб вона стала видною.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { Clock, type LucideIcon } from 'lucide-react';

export interface FinCol { key: string; label: string; strong?: boolean }
export interface FinCell { v: string; color?: string }
export interface FinRow { name: string; Icon?: LucideIcon; color?: string; cells: FinCell[]; strong?: boolean }

export default function FinHourlyTable({
  firstLabel, cols, rows, minWidth, activeColKey, cornerIcon: CornerIcon = Clock, plotLeft, plotRight,
}: {
  firstLabel: string; cols: FinCol[]; rows: FinRow[]; minWidth?: number; activeColKey?: string | number | null;
  cornerIcon?: LucideIcon;
  /** Ширина лівої/правої осі графіка над таблицею (margin + YAxis.width), щоб
      липкі колонки таблиці мали ту саму ширину і години збігалися по вертикалі
      зі стовпчиками графіка. Без цього — довільна ширина за вмістом. */
  plotLeft?: number; plotRight?: number;
}) {
  // Єдиний шрифт чисел — Urbanist із табличними цифрами (як у шапці), без
  // моноширинного «зоопарку». Табличні цифри дають вирівнювання без monospace.
  const cell: CSSProperties = {
    padding: '8px 10px', fontSize: 12.5, textAlign: 'center', color: 'var(--text)',
    borderBottom: '1px solid var(--line)', borderRight: '1px solid var(--line)',
    whiteSpace: 'nowrap', fontFamily: 'Urbanist, Inter, sans-serif', fontVariantNumeric: 'tabular-nums',
  };
  const head: CSSProperties = {
    ...cell, fontWeight: 700, color: 'var(--text-muted)', background: 'var(--surface-2)',
    fontFamily: 'Urbanist, Inter, sans-serif',
  };
  // Липкий перший стовпець (зліва): роздільник тінню (inset), щоб не зникав при скролі.
  const label: CSSProperties = {
    ...cell, textAlign: 'left', fontWeight: 600, color: 'var(--text-muted)',
    background: 'var(--surface-solid)', position: 'sticky', left: 0, zIndex: 2,
    borderRight: 'none', boxShadow: 'inset -1px 0 0 var(--line-strong)',
    paddingLeft: 12, paddingRight: 12, fontFamily: 'Urbanist, Inter, sans-serif',
    ...(plotLeft != null ? { width: plotLeft, minWidth: plotLeft, maxWidth: plotLeft, paddingLeft: 0, paddingRight: 0, textAlign: 'center' as const } : {}),
  };
  // Липкий останній стовпець «Всього» (справа): суцільний фон + роздільник зліва.
  const stickyRight: CSSProperties = {
    position: 'sticky', right: 0, zIndex: 2, background: 'var(--surface-solid)',
    borderRight: 'none', boxShadow: 'inset 1px 0 0 var(--line-strong)',
    ...(plotRight != null ? { width: plotRight, minWidth: plotRight, maxWidth: plotRight } : {}),
  };

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const headRowRef = useRef<HTMLTableRowElement | null>(null);
  const [marker, setMarker] = useState<{ left: number; width: number } | null>(null);

  // Підсвітка: та сама техніка, що й у ForecastMmsTable — offsetLeft/offsetWidth
  // з розкладки, а не з масштабу вікна, тож коректна за будь-якого zoom.
  const measureMarker = useCallback(() => {
    const row = headRowRef.current;
    if (!row || activeColKey == null) { setMarker(null); return; }
    const i = cols.findIndex((c) => c.key === String(activeColKey));
    // +1 — перша колонка службова (значки показників), даними вона не рахується
    const c = i < 0 ? null : (row.children[i + 1] as HTMLElement | undefined);
    if (!c) { setMarker(null); return; }
    setMarker({ left: c.offsetLeft, width: c.offsetWidth });
  }, [activeColKey, cols]);

  useLayoutEffect(() => { measureMarker(); }, [measureMarker]);

  // Ресайз вікна, зміна масштабу, поява/зникнення скролбару — перезаміряємо.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => measureMarker());
    ro.observe(el);
    return () => ro.disconnect();
  }, [measureMarker]);

  // Автоскрол: коли підсвічена колонка виходить за видиму (між липкими краями)
  // зону, докручуємо контейнер так, щоб вона опинилась по центру. Скрол лише
  // тоді, коли колонка дійсно не влазить, — інакше наведення на вже видиму
  // годину смикало б таблицю без потреби.
  useEffect(() => {
    const el = scrollRef.current;
    const row = headRowRef.current;
    if (!el || !row || activeColKey == null) return;
    const i = cols.findIndex((c) => c.key === String(activeColKey));
    const c = i < 0 ? null : (row.children[i + 1] as HTMLElement | undefined);
    if (!c) return;
    const stickyLeftW = (row.children[0] as HTMLElement)?.offsetWidth || 0;
    const stickyRightW = cols[cols.length - 1]?.strong ? ((row.children[cols.length] as HTMLElement)?.offsetWidth || 0) : 0;
    const cellLeft = c.offsetLeft;
    const cellRight = cellLeft + c.offsetWidth;
    const viewLeft = el.scrollLeft + stickyLeftW;
    const viewRight = el.scrollLeft + el.clientWidth - stickyRightW;
    if (cellLeft < viewLeft || cellRight > viewRight) {
      const free = el.clientWidth - stickyLeftW - stickyRightW;
      const target = cellLeft - stickyLeftW - (free - c.offsetWidth) / 2;
      el.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
    }
  }, [activeColKey, cols]);

  return (
    <div className="tile overflow-hidden">
      <div ref={scrollRef} className="overflow-x-auto" style={{ position: 'relative' }}>
        {/* Підсвітка активної колонки — заливка без вертикалі (див. коментар
            вище про ForecastMmsTable). Лежить ПІД таблицею (z-index 0 проти 1),
            тож цифри нічим не перекриті. */}
        {marker && (
          <div aria-hidden style={{
            position: 'absolute', top: 0, bottom: 0,
            left: marker.left, width: marker.width,
            background: 'color-mix(in srgb, var(--text) 7%, transparent)',
            pointerEvents: 'none', zIndex: 0,
          }} />
        )}
        {/* border-collapse: separate — щоб position:sticky на td був надійним у всіх
            браузерах (з collapse подекуди «відклеюється»); бордери лише right+bottom,
            тож border-spacing:0 виглядає ідентично до collapse. */}
        <table style={{
          borderCollapse: 'separate', borderSpacing: 0, width: '100%', minWidth: minWidth ?? 900,
          // table-layout:fixed рiвняє години з графiком (рiвнi колонки замiсть
          // пiдлаштування пiд вмiст), але це саме й стискає числа, коли вони
          // не влазять у рiвну частку (напр. "Плановий дохiд" iз довшими
          // сумами) - тодi лишаємо auto, як було, а не ламаємо читабельнiсть.
          // Вмикається лише коли передано plotLeft (свiдомий вибiр виклику).
          tableLayout: plotLeft != null ? 'fixed' : undefined,
          position: 'relative', zIndex: 1,
        }}>
          {plotLeft != null && (
            <colgroup>
              <col style={{ width: plotLeft }} />
              {cols.map((c) => (
                <col key={c.key} style={c.strong && plotRight != null ? { width: plotRight } : undefined} />
              ))}
            </colgroup>
          )}
          <tbody>
            <tr ref={headRowRef}>
              <td style={label} title={firstLabel}><CornerIcon size={16} strokeWidth={2} style={{ color: 'var(--text-muted)', display: 'block', margin: '0 auto' }} /></td>
              {cols.map((c) => (
                <td key={c.key} style={c.strong ? { ...head, color: 'var(--text)', ...stickyRight } : head}>{c.label}</td>
              ))}
            </tr>
            {rows.map((row, ri) => (
              <tr key={ri}>
                <td style={label} title={row.name}>
                  {row.Icon
                    ? <row.Icon size={16} strokeWidth={2} style={{ color: row.color || 'var(--text-muted)', display: 'block', margin: '0 auto' }} />
                    : (<>{row.color && <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 999, background: row.color, marginRight: 7 }} />}{row.name}</>)}
                </td>
                {row.cells.map((c, ci) => {
                  const isTotal = cols[ci]?.strong;
                  return (
                    <td key={ci} style={{
                      ...cell,
                      color: c.color || row.color || 'var(--text)',
                      fontWeight: 500,  // єдина вага для всіх чисел (без товстих/тонких)
                      ...(isTotal ? stickyRight : {}),
                    }}>{c.v}</td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
